<template>
  <div class="map-preview cb-wrapper">
    <div class="cb-layout-vertical d-flex flex-column" style="min-height: 480px;">
      <!-- ===== TOP BAR ===== -->
      <div class="cb-topbar d-flex justify-content-between align-items-center" style="padding: 16px 20px; border-bottom: 1px solid #f0f0f0; background: #fff;">
        <h4 class="cb-chart-title" style="margin: 0; font-size: 15px; font-weight: 600; color: #1a1a2e;">{{ title }}</h4>
        <div class="d-flex align-items-center">
          <label class="font-weight-bold text-muted mb-0 mr-2" style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px;">Tahun / Waktu:</label>
          <select v-model="localCustomTime" class="cb-select" style="width: auto; padding: 4px 10px; font-size: 12px;" @change="syncConfig">
            <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
      </div>
      
      <!-- ===== MAP CONTENT ===== -->
      <div class="cb-content" style="position: relative; flex: 1; padding: 20px; background: #fafafa; display: flex; flex-direction: column;">
        <div class="map-container" style="position: relative; flex: 1; height: 400px; border-radius: 8px; overflow: hidden; border: 1px solid #e8e8e8; z-index: 1;">
          
          <!-- Loading Overlay -->
          <div v-if="loading" class="cb-loading" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1000; background: rgba(255,255,255,0.8); display: flex; flex-direction: column; align-items: center; justify-content: center;">
            <div class="cb-spinner"></div>
            <span class="mt-2 text-muted" style="font-size: 12px;">Memuat data...</span>
          </div>

          <l-map :zoom="zoom" :center="center" ref="map" style="height: 100%; width: 100%; z-index: 1;">
            <l-geo-json v-if="geojson" :geojson="geojson" :options="geoJsonOptions" :key="indicatorId + '_' + localCustomTime" />
          </l-map>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LMap, LGeoJson } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import axios from 'axios'

// Fix missing Leaflet marker icons which can sometimes silently crash Leaflet in Vue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const dynamicHost = typeof window !== 'undefined' && window.location && window.location.hostname ? window.location.hostname : 'localhost'
const API_BASE = process.env.VUE_APP_API_URL && !process.env.VUE_APP_API_URL.includes('localhost') ? process.env.VUE_APP_API_URL.replace(/\/$/, '') : `http://${dynamicHost}:3008`

export default {
  name: 'MapPreview',
  components: {
    LMap,
    LGeoJson
  },
  props: {
    indicatorId: { type: Number, required: true },
    title: { type: String, default: 'Peta Data' },
    value: {
      type: Object,
      default: () => ({ custom_time: '2021' })
    },
    availableYears: { type: Array, default: () => (['2021']) }
  },
  data() {
    return {
      localCustomTime: this.value.custom_time || '2021',
      zoom: 4,
      center: [-2.5, 118],
      geojson: null,
      loading: false,
      indicator: null
    }
  },
  computed: {
    geoJsonOptions() {
      const _ = this;
      return {
        style: function(feature) {
           return {
               fillColor: _.getColorArea(feature.properties.DATA_VALUE),
               weight: 1,
               opacity: 1,
               color: 'white',
               dashArray: '3',
               fillOpacity: 1
           };
        },
        onEachFeature: function(feature, layer) {
           layer.bindTooltip(feature.properties.ADMIN_NAME + ": " + (feature.properties.DATA_VALUE !== "NA" ? feature.properties.DATA_VALUE : "N/A"));
        }
      }
    }
  },
  watch: {
    indicatorId: {
      immediate: true,
      async handler(val) {
        if (val) {
          await this.fetchIndicator();
          await this.fetchMapData();
        }
      }
    },
    'value.custom_time'(newVal) {
      if (newVal && newVal !== this.localCustomTime) {
        this.localCustomTime = newVal;
        this.fetchMapData();
      }
    }
  },
  methods: {
    syncConfig() {
      this.$emit('input', { ...this.value, custom_time: this.localCustomTime });
      this.fetchMapData();
    },
    async fetchIndicator() {
      try {
        const res = await axios.get(`${API_BASE}/v1/indicator/${this.indicatorId}`)
        this.indicator = res.data.data
      } catch (e) {
        console.error(e)
      }
    },
    async fetchMapData() {
      if (!this.indicatorId) return;
      this.loading = true;
      try {
        const params = new URLSearchParams()
        params.append('indicator_id', this.indicatorId)
        params.append('unit', 'Province')
        params.append('data_periode', 'year')
        params.append('custom_time', this.localCustomTime)
        
        const res = await axios.get(`${API_BASE}/v1/indicator_data/map?${params.toString()}`)
        this.geojson = res.data.data
      } catch (e) {
        console.error(e)
        this.geojson = null
      } finally {
        this.loading = false
        this.$nextTick(() => {
          if (this.$refs.map && this.$refs.map.mapObject) {
            this.$refs.map.mapObject.invalidateSize()
          }
        })
      }
    },
    getColorArea(value) {
      if (!this.indicator || !this.indicator.legends) return '#D9D9D9'
      const legends = this.indicator.legends
      let color = '#D9D9D9'
      let found = false
      if (value !== "NA") {
        for (let v of legends) {
          if (value <= v.value) {
            found = true
            color = v.color
            break
          }
        }
        if (!found && legends.length) {
          color = legends[legends.length - 1].color
        }
      }
      return color
    }
  }
}
</script>

<style>
.map-preview .leaflet-container {
  height: 400px !important;
  width: 100% !important;
  background: #ffffff !important;
}
</style>

<style scoped>
/* Resusing ChartBuilder styles where applicable */

.cb-wrapper {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
}

.cb-filter-group {
  margin-bottom: 18px;
}
.cb-filter-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #F59E0B;
  text-transform: uppercase;
  margin-bottom: 7px;
}
.cb-select {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  color: #333;
  background: #f9f9f9;
  cursor: pointer;
  outline: none;
  appearance: auto;
}
.cb-select:focus { border-color: #F59E0B; }
.cb-content {
  flex: 1;
  padding: 20px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cb-chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 14px;
}
.cb-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #F59E0B;
  min-height: 240px;
}
.cb-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #FEF3C7;
  border-top-color: #F59E0B;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
